import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Users,
  Search,
  Shield,
  ShieldCheck,
  MapPin,
  Mail,
  Building2,
  Calendar,
  RefreshCw,
  Loader2,
  UserCircle,
  Trash2,
  BarChart3,
  TrendingUp,
  Download,
  MessageSquare,
  Clock,
  Ban,
  CheckCircle,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  "Music Supervisor": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Label": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Publisher": "bg-green-500/20 text-green-400 border-green-500/30",
  "CMO": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Artist": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Songwriter": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "Producer": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  "Brand": "bg-red-500/20 text-red-400 border-red-500/30",
  "A&R": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  "Manager": "bg-teal-500/20 text-teal-400 border-teal-500/30",
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [roles, setRoles] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("syncchain_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (roleFilter && roleFilter !== "all") params.append("role", roleFilter);
      
      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    }
  }, [searchQuery, roleFilter, getAuthHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [getAuthHeaders]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data.roles);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  }, []);

  const fetchFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    try {
      const response = await axios.get(`${API}/admin/feedback`, {
        headers: getAuthHeaders()
      });
      setFeedback(response.data.feedback || []);
    } catch (error) {
      console.error("Failed to fetch feedback:", error);
    } finally {
      setFeedbackLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchStats(), fetchRoles(), fetchFeedback()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchStats, fetchRoles, fetchFeedback]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const [updatingUserId, setUpdatingUserId] = useState(null);
  
  const toggleAdminStatus = async (userId, currentStatus, userEmail) => {
    setUpdatingUserId(userId);
    try {
      await axios.post(`${API}/admin/users/role`, {
        user_id: userId,
        is_admin: !currentStatus
      }, {
        headers: getAuthHeaders()
      });
      toast.success(currentStatus ? `Admin access revoked for ${userEmail}` : `Admin access granted to ${userEmail}`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update admin status");
    } finally {
      setUpdatingUserId(null);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      toast.success("User deleted");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const exportUsersCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `syncchain_users_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Users exported successfully");
    } catch (error) {
      toast.error("Failed to export users");
    }
  };

  const toggleDisabled = async (userId, currentlyDisabled, userName) => {
    const action = currentlyDisabled ? "enable" : "disable";
    if (!currentlyDisabled && !window.confirm(`Disable ${userName}? They won't be able to log in or run searches.`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/${action}`, {}, { headers: getAuthHeaders() });
      toast.success(currentlyDisabled ? `${userName} re-enabled` : `${userName} disabled`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const [quotaModal, setQuotaModal] = useState(null);
  const [quotaValues, setQuotaValues] = useState({});

  const openQuotaModal = (u) => {
    const q = u.quotas || {};
    setQuotaValues({
      single_search: q.single_search?.limit ?? 10,
      catalogue_import: q.catalogue_import?.limit ?? 1,
      catalogue_max_tracks: q.catalogue_max_tracks ?? 20,
      batch_csv: q.batch_csv?.limit ?? 10,
      document: q.document?.limit ?? 1,
    });
    setQuotaModal(u);
  };

  const saveQuota = async () => {
    try {
      await axios.post(`${API}/admin/users/${quotaModal.id}/quota`, quotaValues, { headers: getAuthHeaders() });
      toast.success(`Quota updated for ${quotaModal.name}`);
      setQuotaModal(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update quota");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-syncchain-text">Admin Dashboard</h1>
          <p className="text-syncchain-text-secondary text-sm mt-1">
            Manage users and view platform statistics
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportUsersCSV}
            variant="outline"
            size="sm"
            className="border-white/10"
            data-testid="export-users-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => { fetchUsers(); fetchStats(); }}
            variant="outline"
            size="sm"
            className="border-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-syncchain-text-secondary">Total Users</p>
                <p className="text-3xl font-heading font-bold text-syncchain-text mt-1">
                  {stats?.users?.total || 0}
                </p>
              </div>
              <Users className="w-10 h-10 text-syncchain-accent/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-syncchain-text-secondary">New This Week</p>
                <p className="text-3xl font-heading font-bold text-syncchain-success mt-1">
                  +{stats?.users?.recent_signups || 0}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-syncchain-success/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-syncchain-text-secondary">Total Searches</p>
                <p className="text-3xl font-heading font-bold text-syncchain-text mt-1">
                  {stats?.activity?.total_searches || 0}
                </p>
              </div>
              <Search className="w-10 h-10 text-syncchain-info/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-syncchain-text-secondary">Batch Jobs</p>
                <p className="text-3xl font-heading font-bold text-syncchain-text mt-1">
                  {stats?.activity?.total_batches || 0}
                </p>
              </div>
              <BarChart3 className="w-10 h-10 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users by Role */}
      {stats?.users?.by_role && Object.keys(stats.users.by_role).length > 0 && (
        <Card className="bg-syncchain-paper border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-syncchain-text flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-syncchain-accent" />
              Users by Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.users.by_role).map(([role, count]) => (
                <Badge
                  key={role}
                  variant="outline"
                  className={`${ROLE_COLORS[role] || "bg-white/10 text-white"} py-2 px-4 text-sm`}
                >
                  {role}: <span className="font-bold ml-1">{count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Feedback Section */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading text-syncchain-text flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-syncchain-accent" />
              User Feedback ({feedback.length})
            </CardTitle>
            <Button
              onClick={fetchFeedback}
              variant="outline"
              size="sm"
              className="border-white/10"
              disabled={feedbackLoading}
            >
              {feedbackLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {feedback.length === 0 ? (
            <div className="text-center py-8 text-syncchain-text-muted">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No feedback submissions yet</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {feedback.map((item, index) => (
                <div
                  key={item.id || index}
                  className="p-4 bg-syncchain-surface rounded-lg border border-white/5"
                  data-testid={`feedback-item-${index}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-syncchain-text whitespace-pre-wrap">{item.message}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-syncchain-text-muted">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {item.user_email || "Anonymous"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {item.created_at ? new Date(item.created_at).toLocaleString() : "Unknown"}
                        </span>
                        {item.trigger && (
                          <Badge variant="outline" className="text-xs bg-white/5 border-white/10">
                            {item.trigger}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-lg font-heading text-syncchain-text flex items-center gap-2">
              <Users className="w-5 h-5 text-syncchain-accent" />
              All Users ({users.length})
            </CardTitle>
            
            <div className="flex gap-3">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search users..."
                    className="pl-9 w-48 h-9 bg-syncchain-surface border-white/10 text-syncchain-text"
                  />
                </div>
              </form>
              
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); }}>
                <SelectTrigger className="w-40 h-9 bg-syncchain-surface border-white/10 text-syncchain-text">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-syncchain-text-secondary">User</TableHead>
                  <TableHead className="text-syncchain-text-secondary">Role</TableHead>
                  <TableHead className="text-syncchain-text-secondary">Quota</TableHead>
                  <TableHead className="text-syncchain-text-secondary">Status</TableHead>
                  <TableHead className="text-syncchain-text-secondary">Joined</TableHead>
                  <TableHead className="text-syncchain-text-secondary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-white/5 hover:bg-white/5">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-syncchain-accent/20 flex items-center justify-center">
                          <span className="text-syncchain-accent font-semibold">
                            {u.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-syncchain-text">{u.name}</span>
                            {u.is_admin && (
                              <ShieldCheck className="w-4 h-4 text-syncchain-accent" title="Admin" />
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-syncchain-text-muted">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </div>
                          {u.company && (
                            <div className="flex items-center gap-1 text-xs text-syncchain-text-muted">
                              <Building2 className="w-3 h-3" />
                              {u.company}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`${ROLE_COLORS[u.role] || "bg-white/10 text-white"}`}
                      >
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.is_admin ? (
                        <span className="text-xs text-syncchain-accent font-medium">Unlimited</span>
                      ) : (
                        <div className="text-xs text-syncchain-text-secondary space-y-0.5">
                          <div>Songs: {u.quotas?.single_search?.used ?? 0}/{u.quotas?.single_search?.limit ?? 10}</div>
                          <div>Catalogue: {u.quotas?.catalogue_import?.used ?? 0}/{u.quotas?.catalogue_import?.limit ?? 1}</div>
                          <div>Batch: {u.quotas?.batch_csv?.used ?? 0}/{u.quotas?.batch_csv?.limit ?? 10}</div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.is_disabled ? (
                        <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/30 text-xs">Disabled</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-500/15 text-green-400 border-green-500/30 text-xs">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-syncchain-text-secondary text-sm">
                        <Calendar className="w-3 h-3" />
                        {formatDate(u.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openQuotaModal(u)}
                          className="text-syncchain-text-muted hover:text-syncchain-accent"
                          title="Edit quota"
                          disabled={u.is_admin}
                          data-testid={`edit-quota-${u.id}`}
                        >
                          <SlidersHorizontal className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleDisabled(u.id, u.is_disabled, u.name)}
                          className={u.is_disabled ? "text-green-400 hover:text-green-300" : "text-yellow-400 hover:text-yellow-300"}
                          title={u.is_disabled ? "Enable user" : "Disable user"}
                          disabled={u.id === user?.id || u.is_admin}
                          data-testid={`toggle-disabled-${u.id}`}
                        >
                          {u.is_disabled ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAdminStatus(u.id, u.is_admin, u.email)}
                          className={u.is_admin ? "text-syncchain-accent" : "text-syncchain-text-muted"}
                          title={u.is_admin ? "Revoke admin" : "Grant admin"}
                          disabled={u.id === user?.id || updatingUserId === u.id}
                          data-testid={`toggle-admin-${u.id}`}
                        >
                          {updatingUserId === u.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : u.is_admin ? (
                            <ShieldCheck className="w-4 h-4" />
                          ) : (
                            <Shield className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(u.id, u.name)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          disabled={u.id === user?.id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-syncchain-text-muted">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Quota Edit Modal */}
      <Dialog open={!!quotaModal} onOpenChange={(open) => { if (!open) setQuotaModal(null); }}>
        <DialogContent className="bg-syncchain-paper border-white/10 text-syncchain-text sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-syncchain-text font-heading">
              Edit Quota — {quotaModal?.name}
            </DialogTitle>
            <DialogDescription className="text-syncchain-text-secondary text-sm">
              Set usage limits for this account. Changes take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <Label className="text-syncchain-text-secondary text-sm">Single Song Searches</Label>
                <Input
                  type="number"
                  min={0}
                  value={quotaValues.single_search ?? 10}
                  onChange={(e) => setQuotaValues(v => ({ ...v, single_search: parseInt(e.target.value) || 0 }))}
                  className="w-24 h-8 bg-syncchain-surface border-white/10 text-syncchain-text text-center"
                  data-testid="quota-single-search-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-syncchain-text-secondary text-sm">Catalogue Imports</Label>
                <Input
                  type="number"
                  min={0}
                  value={quotaValues.catalogue_import ?? 1}
                  onChange={(e) => setQuotaValues(v => ({ ...v, catalogue_import: parseInt(e.target.value) || 0 }))}
                  className="w-24 h-8 bg-syncchain-surface border-white/10 text-syncchain-text text-center"
                  data-testid="quota-catalogue-import-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-syncchain-text-secondary text-sm">Max Tracks per Import</Label>
                <Input
                  type="number"
                  min={1}
                  value={quotaValues.catalogue_max_tracks ?? 20}
                  onChange={(e) => setQuotaValues(v => ({ ...v, catalogue_max_tracks: parseInt(e.target.value) || 1 }))}
                  className="w-24 h-8 bg-syncchain-surface border-white/10 text-syncchain-text text-center"
                  data-testid="quota-max-tracks-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-syncchain-text-secondary text-sm">Batch CSV Uploads</Label>
                <Input
                  type="number"
                  min={0}
                  value={quotaValues.batch_csv ?? 10}
                  onChange={(e) => setQuotaValues(v => ({ ...v, batch_csv: parseInt(e.target.value) || 0 }))}
                  className="w-24 h-8 bg-syncchain-surface border-white/10 text-syncchain-text text-center"
                  data-testid="quota-batch-csv-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-syncchain-text-secondary text-sm">Document Imports</Label>
                <Input
                  type="number"
                  min={0}
                  value={quotaValues.document ?? 1}
                  onChange={(e) => setQuotaValues(v => ({ ...v, document: parseInt(e.target.value) || 0 }))}
                  className="w-24 h-8 bg-syncchain-surface border-white/10 text-syncchain-text text-center"
                  data-testid="quota-document-input"
                />
              </div>
            </div>
            {quotaModal && !quotaModal.is_admin && (
              <div className="text-xs text-syncchain-text-muted mt-1 p-2 rounded bg-white/5 border border-white/5">
                Current usage: {quotaModal.quotas?.single_search?.used ?? 0} searches, {quotaModal.quotas?.catalogue_import?.used ?? 0} imports, {quotaModal.quotas?.batch_csv?.used ?? 0} batches
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuotaModal(null)}
              className="border-white/10 text-syncchain-text-secondary"
              data-testid="quota-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={saveQuota}
              className="bg-syncchain-accent text-syncchain-bg hover:bg-syncchain-accent/90"
              data-testid="quota-save-btn"
            >
              Save Quota
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
